import React, { useState, memo } from 'react';
import { Button, Tooltip } from 'gatorcomponents';
import * as Styles from '../../ServerList.styles';
import { useIsMobile } from '@/hooks/useIsMobile';
import ServerStatus from '../ServerStatus';
import ServerNickname from '../ServerNickname';
import FavoriteButton from '../FavoriteButton';
import QuickAccess from '../QuickAccess';
import useLocale from '@/hooks/useLocale';

const ServerListItem = memo(({
  server,
  onGoToManagement,
  onUpdateFavorite,
  onUpdateNickname,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const isMobile = useIsMobile();

  const { productDetails: productDetailsLocale } = useLocale();
  const { advancedHosting: advancedHostingLocale } = productDetailsLocale || {};
  const { serverList: serverListLocale } = advancedHostingLocale || {};

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleToggleFavorite = () => {
    onUpdateFavorite(server.id, !server.isFavorite);
  };

  const handleManagementClick = () => {
    onGoToManagement(server.id);
  };

  return (
    <Styles.ListItem data-testid={`server-list-item-${server.id}`}>
      <Styles.Cell>
        <Styles.CellContent>
          <Styles.ServerName data-testid={`server-product-name-${server.id}`}>
            {server.productName}
          </Styles.ServerName>
          <Styles.ServerDomain data-testid={`server-domain-${server.id}`}>{server.domain}</Styles.ServerDomain>
          <ServerNickname
            serverId={server.id}
            nickname={server.nickName}
            placeholder={serverListLocale?.columnsLabel?.setServerNickname}
            onUpdateNickname={onUpdateNickname}
          />
        </Styles.CellContent>
        {isMobile && (
          <FavoriteButton
            isFavorite={server.isFavorite}
            isHovered={isHovered}
            onToggle={handleToggleFavorite}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        )}
      </Styles.Cell>

      <Styles.Cell>
        <Styles.CellContent>
          {isMobile && <Styles.CellLabel>{serverListLocale?.columnsLabel?.addressIp}</Styles.CellLabel>}
          <Styles.ServerIp data-testid={`server-ip-${server.id}`}>{server.ip}</Styles.ServerIp>
        </Styles.CellContent>
        {isMobile && <ServerStatus serverId={server.id} />}
      </Styles.Cell>

      {!isMobile && (
        <Styles.Cell>
          <ServerStatus
            serverInfo={server.serverInfo}
            serverId={server.id}
          />
        </Styles.Cell>
      )}

      <Styles.Cell>
        {!server.serverInfo?.locked && (
          <Styles.CellContent>
            {(isMobile && server.applications.length > 0) && (
            <Styles.CellLabel>{serverListLocale?.columnsLabel?.quickAccess}</Styles.CellLabel>
            )}
            <QuickAccess
              locale={serverListLocale}
              applications={server.applications}
              productId={server.id}
              serverIp={server.ip}
              domain={server.domain}
            />
          </Styles.CellContent>
        )}
      </Styles.Cell>

      <Styles.Cell align="right">
        <Button
          label={serverListLocale?.buttons?.management}
          onClick={handleManagementClick}
          variant="secondary"
          width={isMobile ? 'full' : 'fit-content'}
          size="large"
          version="v2"
          testId={`server-management-button-${server.id}`}
        />
        {!isMobile && (
          <Tooltip content={serverListLocale?.tooltip?.favorite} preferredPosition="bottomRight">
            <FavoriteButton
              isFavorite={server.isFavorite}
              isHovered={isHovered}
              onToggle={handleToggleFavorite}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            />
          </Tooltip>
        )}
      </Styles.Cell>
    </Styles.ListItem>
  );
});

ServerListItem.displayName = 'ServerListItem';

export default ServerListItem;
